import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback, useRef } from "react";
import CourierLayout from "../components/CourierLayout";
import { useAuth } from "../App";
import {
  Package, Truck, Clock, CheckCircle2, AlertTriangle,
  MapPin, ArrowRight, Eye, X, Filter, UserPlus, Inbox, QrCode, Copy, Navigation,
  Camera, PenTool, Trash2, Image as ImageIcon
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const STATUSES = [
  { value: "", label: "Tous" },
  { value: "created", label: "Disponible", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "assigned", label: "Assignée", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "picked_up", label: "Recuperee", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  { value: "in_transit", label: "En transit", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  { value: "delivered", label: "Livree", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "completed", label: "Terminée", color: "bg-green-100 text-green-700 border-green-200" },
  { value: "failed", label: "Echouee", color: "bg-red-100 text-red-700 border-red-200" },
];

const STATUS_LABELS = Object.fromEntries(STATUSES.filter(s => s.value).map(s => [s.value, s.label]));
const getStatusStyle = (s) => STATUSES.find(st => st.value === s)?.color || "bg-slate-100 text-slate-500 border-slate-200";

const ALLOWED_NEXT = {
  assigned: ["picked_up", "failed"],
  picked_up: ["in_transit", "failed"],
  in_transit: ["delivered", "failed"],
  delivered: ["completed"],
};

const COURSE_TABS = [
  { value: "", label: "Tout", icon: Package },
  { value: "assurance", label: "Expéditions Assurance", icon: Truck },
  { value: "generale", label: "Courses Generales", icon: Package },
];

export default function CourierCoursesPage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [showDetail, setShowDetail] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(null);
  const [statusNote, setStatusNote] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [courseType, setCourseType] = useState("");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (courseType) params.course_type = courseType;
      const res = await axios.get(`${API}/log/courses`, { headers: getHeaders(), params });
      setCourses(res.data.courses || []);
    } catch { }
    finally { setLoading(false); }
  }, [filterStatus, courseType]);

  useEffect(() => { load(); }, [load]);

  const [proofPhoto, setProofPhoto] = useState(null);
  const [proofSignature, setProofSignature] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const sigCanvasRef = useRef(null);
  const sigDrawing = useRef(false);
  const sigLastPos = useRef({ x: 0, y: 0 });

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Photo trop volumineuse (max 3 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setProofPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const initSignature = () => {
    setShowSignaturePad(true);
    setTimeout(() => {
      const canvas = sigCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }, 100);
  };

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0];
    return {
      x: (touch ? touch.clientX : e.clientX) - rect.left,
      y: (touch ? touch.clientY : e.clientY) - rect.top
    };
  };

  const sigStart = (e) => {
    e.preventDefault();
    sigDrawing.current = true;
    sigLastPos.current = getPos(e, sigCanvasRef.current);
  };

  const sigMove = (e) => {
    e.preventDefault();
    if (!sigDrawing.current) return;
    const canvas = sigCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(sigLastPos.current.x, sigLastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    sigLastPos.current = pos;
  };

  const sigEnd = () => { sigDrawing.current = false; };

  const saveSignature = () => {
    const canvas = sigCanvasRef.current;
    if (canvas) {
      setProofSignature(canvas.toDataURL("image/png", 0.6));
      setShowSignaturePad(false);
    }
  };

  const clearSignature = () => {
    const canvas = sigCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  };

  const updateStatus = async (courseId, newStatus) => {
    try {
      await axios.put(`${API}/log/courses/${courseId}/status`, {
        status: newStatus,
        note: statusNote,
        proof_photo: proofPhoto || null,
        proof_signature: proofSignature || null,
      }, { headers: getHeaders() });
      toast.success(`Statut: ${STATUS_LABELS[newStatus]}`);
      setShowStatusModal(null);
      setStatusNote("");
      setProofPhoto(null);
      setProofSignature(null);
      setShowSignaturePad(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const selfAssign = async (courseId) => {
    setUpdatingId(courseId);
    try {
      await axios.put(`${API}/log/courses/${courseId}/self-assign`, {}, { headers: getHeaders() });
      toast.success("Course prise en charge !");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setUpdatingId(null); }
  };

  const loadDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/log/courses/${id}`, { headers: getHeaders() });
      setShowDetail(res.data.course);
    } catch (e) { toast.error("Erreur chargement"); }
  };

  const isPool = (c) => !c.courier_id || c.courier_id === "";
  const isMine = (c) => c.courier_id === user?.id;

  return (
    <CourierLayout title="Mes courses" subtitle="Expéditions assurance et courses generales">
      <div data-testid="courier-courses-page" className="space-y-4">
        {/* Course type tabs */}
        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
          {COURSE_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.value} onClick={() => { setCourseType(tab.value); setLoading(true); }}
                data-testid={`type-tab-${tab.value || "all"}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  courseType === tab.value ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "text-slate-500 hover:text-slate-800"
                }`}>
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-slate-500" />
          {STATUSES.map(s => (
            <button key={s.value} onClick={() => { setFilterStatus(s.value); setLoading(true); }}
              data-testid={`filter-${s.value || "all"}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                filterStatus === s.value
                  ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                  : "bg-white text-slate-500 border border-slate-200 hover:border-slate-300"
              }`}>{s.label}</button>
          ))}
        </div>

        {/* Courses list */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" /></div>
        ) : courses.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200 shadow-sm">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Aucune course trouvee</p>
          </div>
        ) : (
          <div className="space-y-2">
            {courses.map(course => {
              const pool = isPool(course);
              const mine = isMine(course);
              const priorityColor = course.priority === "critique" ? "text-red-600" : course.priority === "urgent" ? "text-amber-600" : "text-slate-500";
              return (
                <div key={course.id} data-testid={`course-item-${course.id}`}
                  className={`border rounded-xl p-4 transition-colors shadow-sm ${
                    pool ? "bg-amber-50 border-amber-200 hover:border-amber-300" : "bg-white border-slate-200 hover:border-emerald-300"
                  }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${course.course_type === "assurance" ? "bg-blue-100" : "bg-violet-100"}`}>
                        {course.course_type === "assurance" ? <Truck className="w-4 h-4 text-blue-600" /> : <Package className="w-4 h-4 text-violet-600" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-slate-900 truncate">{course.title}</p>
                          <span className={`text-[10px] font-medium ${priorityColor}`}>[{course.priority}]</span>
                          {pool && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Disponible</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                          <MapPin className="w-3 h-3" /><span className="truncate">{course.pickup_address}</span>
                          <span className="text-slate-400">→</span>
                          <span className="truncate">{course.delivery_address}</span>
                        </div>
                        <p className="text-[10px] text-slate-600 mt-0.5">{new Date(course.created_at).toLocaleDateString("fr-FR")} - {course.created_by_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => loadDetail(course.id)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-emerald-600" data-testid={`view-detail-${course.id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                      {pool ? (
                        <Button size="sm" disabled={updatingId === course.id} onClick={() => selfAssign(course.id)}
                          className="bg-amber-600 hover:bg-amber-500/100/100 text-white text-xs gap-1" data-testid={`self-assign-${course.id}`}>
                          <UserPlus className="w-3.5 h-3.5" /> Prendre
                        </Button>
                      ) : mine ? (
                        <button onClick={() => { setShowStatusModal(course); setStatusNote(""); }}
                          className={`px-2.5 py-1 rounded-full border text-[11px] font-medium cursor-pointer hover:opacity-80 ${getStatusStyle(course.status)}`}
                          data-testid={`status-btn-${course.id}`}>
                          {STATUS_LABELS[course.status] || course.status}
                        </button>
                      ) : (
                        <span className={`px-2.5 py-1 rounded-full border text-[11px] font-medium ${getStatusStyle(course.status)}`}>
                          {STATUS_LABELS[course.status] || course.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Status Update Modal with Proof */}
        {showStatusModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="courier-status-modal">
            <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="text-base font-bold text-slate-900">Modifier le statut</h2>
                <button onClick={() => { setShowStatusModal(null); setProofPhoto(null); setProofSignature(null); setShowSignaturePad(false); }} className="text-slate-400 hover:text-slate-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Course</p>
                  <p className="text-sm font-medium text-slate-900">{showStatusModal.title}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Statut actuel: <span className="font-medium text-slate-700">{STATUS_LABELS[showStatusModal.status]}</span></p>
                </div>

                {/* Proof Section */}
                {(ALLOWED_NEXT[showStatusModal.status] || []).some(s => ["picked_up", "delivered", "completed"].includes(s)) && (
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 space-y-3" data-testid="proof-section">
                    <p className="text-xs font-bold text-sky-800 flex items-center gap-1.5">
                      <Camera className="w-3.5 h-3.5" /> Preuves de collecte / livraison
                    </p>
                    <p className="text-[10px] text-sky-600">Ajoutez une photo et/ou une signature comme preuve</p>

                    <div className="flex gap-2">
                      {/* Photo capture */}
                      <label className="flex-1 cursor-pointer" data-testid="proof-photo-btn">
                        <div className={`border-2 border-dashed rounded-xl p-3 text-center transition-colors ${proofPhoto ? "border-emerald-300 bg-emerald-50" : "border-slate-300 hover:border-sky-400"}`}>
                          {proofPhoto ? (
                            <div className="relative">
                              <img src={proofPhoto} alt="Preuve" className="w-full h-20 object-cover rounded-lg" />
                              <button onClick={(e) => { e.preventDefault(); setProofPhoto(null); }} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5" data-testid="remove-photo">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <Camera className="w-5 h-5 mx-auto text-slate-400 mb-1" />
                              <p className="text-[10px] text-slate-500">Photo</p>
                            </>
                          )}
                        </div>
                        <input type="file" accept="image/*" capture="environment" onChange={handlePhotoCapture} className="hidden" />
                      </label>

                      {/* Signature */}
                      <div className="flex-1" data-testid="proof-signature-btn">
                        <button onClick={() => proofSignature ? setProofSignature(null) : initSignature()}
                          className={`w-full border-2 border-dashed rounded-xl p-3 text-center transition-colors ${proofSignature ? "border-emerald-300 bg-emerald-50" : "border-slate-300 hover:border-sky-400"}`}>
                          {proofSignature ? (
                            <div className="relative">
                              <img src={proofSignature} alt="Signature" className="w-full h-20 object-contain rounded-lg bg-white" />
                              <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                                <Trash2 className="w-3 h-3" />
                              </div>
                            </div>
                          ) : (
                            <>
                              <PenTool className="w-5 h-5 mx-auto text-slate-400 mb-1" />
                              <p className="text-[10px] text-slate-500">Signature</p>
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Signature Pad */}
                    {showSignaturePad && (
                      <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2" data-testid="signature-pad">
                        <p className="text-[10px] text-slate-500 font-medium">Signez ci-dessous :</p>
                        <canvas ref={sigCanvasRef}
                          className="w-full h-32 border border-slate-300 rounded-lg cursor-crosshair touch-none"
                          onMouseDown={sigStart} onMouseMove={sigMove} onMouseUp={sigEnd} onMouseLeave={sigEnd}
                          onTouchStart={sigStart} onTouchMove={sigMove} onTouchEnd={sigEnd}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={clearSignature} className="text-xs flex-1" data-testid="clear-signature">
                            <Trash2 className="w-3 h-3 mr-1" /> Effacer
                          </Button>
                          <Button size="sm" onClick={saveSignature} className="text-xs flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-signature">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Valider
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Status buttons */}
                <div className="space-y-2">
                  {(ALLOWED_NEXT[showStatusModal.status] || []).map(s => (
                    <button key={s} onClick={() => updateStatus(showStatusModal.id, s)} data-testid={`set-status-${s}`}
                      className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                        s === "failed" ? "border-red-200 text-red-600 bg-red-50 hover:bg-red-100" : "border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                      }`}>{STATUS_LABELS[s]}</button>
                  ))}
                  {!(ALLOWED_NEXT[showStatusModal.status] || []).length && (
                    <p className="text-xs text-slate-500 text-center py-4">Aucune transition disponible</p>
                  )}
                </div>

                {/* Note */}
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Note (optionnel)</label>
                  <input data-testid="status-note-input" value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="Commentaire..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="courier-detail-modal">
            <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">{showDetail.title}</h2>
                <button onClick={() => setShowDetail(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-[10px] text-slate-500 uppercase">Type</p><p className="text-sm text-slate-900">{showDetail.course_type === "assurance" ? "Assurance" : "Generale"}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Priorite</p><p className="text-sm text-slate-900">{showDetail.priority}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Recuperation</p><p className="text-sm text-slate-900">{showDetail.pickup_address}</p>{showDetail.pickup_contact && <p className="text-[10px] text-slate-500">{showDetail.pickup_contact} {showDetail.pickup_phone}</p>}</div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Livraison</p><p className="text-sm text-slate-900">{showDetail.delivery_address}</p>{showDetail.delivery_contact && <p className="text-[10px] text-slate-500">{showDetail.delivery_contact} {showDetail.delivery_phone}</p>}</div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Cree par</p><p className="text-sm text-slate-900">{showDetail.created_by_name}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Date</p><p className="text-sm text-slate-900">{new Date(showDetail.created_at).toLocaleDateString("fr-FR")}</p></div>
                </div>
                {showDetail.description && (
                  <div><p className="text-[10px] text-slate-500 uppercase">Description</p><p className="text-sm text-slate-700">{showDetail.description}</p></div>
                )}
                {showDetail.return_code && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="return-code-block">
                    <div className="flex items-center gap-2 mb-3">
                      <QrCode className="w-4 h-4 text-emerald-600" />
                      <p className="text-xs text-emerald-700 font-semibold uppercase">Code de retour - Liasse documents</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(showDetail.return_code)}`}
                        alt="QR Code" className="w-24 h-24 rounded-lg border border-emerald-200" data-testid="qr-code-image" />
                      <div>
                        <span className="text-xl font-mono font-bold text-slate-900 tracking-widest block">{showDetail.return_code}</span>
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => { navigator.clipboard.writeText(showDetail.return_code); toast.success("Code copie !"); }}
                            className="px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 rounded-lg text-emerald-700 transition-colors text-xs flex items-center gap-1.5" data-testid="copy-return-code">
                            <Copy className="w-3.5 h-3.5" /> Copier
                          </button>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-2">Scannez ou communiquez ce code a la pharmacie</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Proofs from tracking */}
                {showDetail.tracking?.some(t => t.proof_photo || t.proof_signature) && (
                  <div data-testid="proofs-section">
                    <p className="text-xs text-slate-500 font-semibold mb-2 uppercase flex items-center gap-1.5">
                      <ImageIcon className="w-3.5 h-3.5" /> Preuves collectees
                    </p>
                    <div className="space-y-3">
                      {showDetail.tracking.filter(t => t.proof_photo || t.proof_signature).map((t, i) => (
                        <div key={i} className="bg-sky-50 border border-sky-200 rounded-xl p-3">
                          <p className="text-[10px] text-sky-700 font-medium mb-2">
                            {STATUS_LABELS[t.status] || t.status} — {new Date(t.date).toLocaleString("fr-FR")}
                          </p>
                          <div className="flex gap-3">
                            {t.proof_photo && (
                              <div data-testid={`proof-photo-${t.status}`}>
                                <p className="text-[9px] text-slate-500 mb-1">Photo</p>
                                <img src={t.proof_photo} alt="Preuve photo" className="w-28 h-20 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-80"
                                  onClick={() => window.open(t.proof_photo, '_blank')} />
                              </div>
                            )}
                            {t.proof_signature && (
                              <div data-testid={`proof-signature-${t.status}`}>
                                <p className="text-[9px] text-slate-500 mb-1">Signature</p>
                                <img src={t.proof_signature} alt="Signature" className="w-28 h-20 object-contain rounded-lg border border-slate-200 bg-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showDetail.tracking?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 font-semibold mb-2 uppercase">Suivi</p>
                    <div className="space-y-2">
                      {showDetail.tracking.map((t, i) => (
                        <div key={i} className="flex items-start gap-3 p-2 bg-slate-50 rounded-lg border border-slate-100">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${getStatusStyle(t.status).split(" ")[0]}`} />
                          <div>
                            <p className="text-xs text-slate-900">{STATUS_LABELS[t.status] || t.status} <span className="text-slate-500 ml-1">{t.note}</span></p>
                            <p className="text-[10px] text-slate-500">{new Date(t.date).toLocaleString("fr-FR")} - {t.by}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </CourierLayout>
  );
}
